import { defineStore } from "pinia";
import { ref } from "vue";
import { INSPECTION_LINKS_ENDPOINT } from "@/constants/Endpoints";
import axios from "axios";
import { LOGGER } from "@/util/logger";
import { clone } from "@/util/clone";
import { MODULE_TYPES } from "@/constants/InspectionConstants";
import { useInspectionStore } from "@/stores/inspection";
import { useConfigStore } from "@/stores/config";
import { REFERENCE_TABLE } from "@/constants/ReferenceTables";
import { STRUCTURE_UNIT_TYPE } from "@/constants/Inspections";

export const useJointsStore = defineStore("joints", () => {
  let pageData = ref({});
  const inspectionStore = useInspectionStore();
  const configStore = useConfigStore();

  const loadDataObject = async (moduleType, brkey, inspectionId, structure) => {
    if (moduleType === MODULE_TYPES.INSPECTION) {
      //if moduleType is inspection, load data from pouchDB
      convertToInspModuleObject(structure);
    } else if (moduleType === MODULE_TYPES.MANAGEMENT && brkey && inspectionId) {
      //if moduleType is management, load data from service call
      await getInspLinkDataObject(brkey, inspectionId);
    }
  };

  const getJoints = () => {
    return clone(pageData.value);
  };

  const saveData = async (moduleType, value, structure) => {
    let isSuccess = false;
    if (moduleType === MODULE_TYPES.INSPECTION) {
      convertToSaveInspModuleObject(value, structure);
      isSuccess = true;
    } else if (moduleType === MODULE_TYPES.MANAGEMENT) {
      isSuccess = await updateData(convertToSaveInspLinkObject(value));
    }
    return isSuccess;
  };

  const updateData = async (payload) => {
    let isSuccess = true;
    const url = INSPECTION_LINKS_ENDPOINT.UPDATE_JOINTS;
    await axios
      .put(url, payload)
      .then((response) => {
        LOGGER.logEvent(LOGGER.EventAction.Update, LOGGER.EventCat.RequestResponse, "Update Joints", response);
        convertToInspLinkObject(response.data.data);
      })
      .catch((e) => {
        LOGGER.logException(e);
        isSuccess = false;
      });
    return isSuccess;
  };

  const convertToSaveInspModuleObject = (joints, structure) => {
    structure.structureJoints = joints.structureJoints;
    structure.userInspection.overallJointCondRating = joints.overallCondRating;
  };

  const getInspLinkDataObject = async (brkey, inspectionId) => {
    pageData.value = {};
    let url = INSPECTION_LINKS_ENDPOINT.GET_JOINTS.replace("{brkey}", brkey).replace("{inspectionId}", inspectionId);
    await axios
      .get(url)
      .then((response) => {
        convertToInspLinkObject(response.data.data);
      })
      .catch((e) => {
        LOGGER.logException(e);
      });
  };

  const convertToInspModuleObject = (structure) => {
    pageData.value = {
      brkey: structure.inspection.brkey,
      inspectionId: structure.inspection.inspectionId,
      overallCondRating: structure.userInspection.overallJointCondRating,
      structureJoints: structure.structureJoints,
      jointLocations: convertStructureUnitsToJointLocations(
        inspectionStore.getFilteredStructureUnits([STRUCTURE_UNIT_TYPE.APRAS_SPAN], false),
      ),
    };
  };

  const convertStructureUnitsToJointLocations = (structureUnits) => {
    return structureUnits
      ?.filter((b) => ["A", "M", "B", "C", "P", "W"].includes(b.legacyStructureType))
      ?.map((b) => ({
        title: `${configStore.getReferenceValue(REFERENCE_TABLE.STRUCTURE_UNIT_TYPE, b.legacyStructureType, true)}-${
          b.strUnitLabel
        }`,
        value: b.strUnitKey,
      }))
      .sort((a, b) => a.title.localeCompare(b.title));
  };

  const convertToSaveInspLinkObject = (joints) => {
    return {
      brkey: joints.brkey,
      inspectionId: joints.inspectionId,
      overallJointCondRating: joints.overallCondRating,
      structureJoints: joints.structureJoints,
      inspectionComments: joints.inspectionComments,
    };
  };

  const convertToInspLinkObject = (response) => {
    pageData.value = {
      brkey: response.brkey,
      inspectionId: response.inspectionId,
      overallCondRating: response.overallJointCondRating,
      structureJoints: response.structureJoints,
      jointLocations: convertStructureUnitsToJointLocations(response.structureUnits),
      inspectionComments: response.inspectionComments,
    };
  };

  return {
    getJoints,
    saveData,
    loadDataObject,
    configStore,
    REFERENCE_TABLE,
    inspectionStore,
  };
});
